import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronRight, Clock, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useTrialUsage } from '@/api/trial'
import { EndTrialNowDialog } from '@/components/manager/subscription/EndTrialNowDialog'

interface Props {
  companyId: number
  isTrialing: boolean
}

export function TrialUsageCard({ companyId, isTrialing }: Props) {
  const { t } = useTranslation()
  const { data: usage } = useTrialUsage(companyId, isTrialing)
  const [activateOpen, setActivateOpen] = useState(false)

  if (!isTrialing || !usage || usage.is_unlimited) return null

  const minutesPercentage = usage.minutes_limit
    ? Math.round((usage.minutes_used / usage.minutes_limit) * 100)
    : 0
  const isNearLimit = minutesPercentage >= 80
  const isLimitReached = minutesPercentage >= 100

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      className={cn(
        'rounded-xl border px-4 py-3',
        isLimitReached
          ? 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800/50'
          : isNearLimit
            ? 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800/50'
            : 'bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800/50',
      )}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-4">
          <div
            className={cn(
              'hidden sm:flex items-center justify-center w-8 h-8 rounded-lg',
              isLimitReached
                ? 'bg-red-100 dark:bg-red-900/50'
                : isNearLimit
                  ? 'bg-amber-100 dark:bg-amber-900/50'
                  : 'bg-blue-100 dark:bg-blue-900/50',
            )}
          >
            <Sparkles
              className={cn(
                'w-4 h-4',
                isLimitReached
                  ? 'text-red-600'
                  : isNearLimit
                    ? 'text-amber-600'
                    : 'text-blue-600',
              )}
            />
          </div>

          <span
            className={cn(
              'text-sm font-medium',
              isLimitReached
                ? 'text-red-700 dark:text-red-300'
                : isNearLimit
                  ? 'text-amber-700 dark:text-amber-300'
                  : 'text-blue-700 dark:text-blue-300',
            )}
          >
            {t('manager.overview.trial.title')}
          </span>

          <div className="hidden sm:block w-px h-5 bg-slate-300 dark:bg-slate-600" />

          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1.5">
              <Clock
                className={cn(
                  'w-3.5 h-3.5',
                  minutesPercentage >= 100
                    ? 'text-red-500'
                    : minutesPercentage >= 80
                      ? 'text-amber-500'
                      : 'text-slate-500',
                )}
              />
              <span className="font-mono font-semibold tabular-nums text-slate-700 dark:text-slate-200">
                {usage.minutes_used}
              </span>
              <span className="text-slate-400">/</span>
              <span className="font-mono tabular-nums text-slate-500">
                {usage.minutes_limit} {t('manager.overview.trial.minutesAbbr')}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => setActivateOpen(true)}
            className="h-8 px-4 text-xs font-semibold bg-[#0A192F] hover:bg-[#0A192F]/90 text-white dark:bg-white dark:text-[#0A192F] dark:hover:bg-white/90"
          >
            {t('manager.overview.trial.activate')}
            <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      </div>

      <AnimatePresence>
        {isLimitReached && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2 pt-2 border-t border-red-200 dark:border-red-800/50 text-xs text-red-600 dark:text-red-400"
          >
            {t('manager.overview.trial.limitReached')}
          </motion.p>
        )}
      </AnimatePresence>

      <EndTrialNowDialog open={activateOpen} onOpenChange={setActivateOpen} />
    </motion.div>
  )
}
